import { get as getByPath } from 'lodash';
import { BaseApiService } from '../BaseApiService';
/**
 * @typedef {import('@goodt-wcore/mixins').ITransportMixinInstance} ITransportMixinInstance
 */

const PUBLIC_ACCESSOR_NAME = '$apiService';
const API_URL_ACCESSOR_NAME = 'apiBaseURL';

/**
 * Creates api base url builder function
 * 1. to build api base url initially in PLAYER
 * 2. watch and rebuild url in EDITOR env
 *
 * @param vm
 * @param {(function(): string) | string} apiBaseURL
 * @return {function(): string}
 */
const createApiBaseURLBuilder = (vm, apiBaseURL) => {
    // prettier-ignore
    const apiBaseURLBuilder = typeof apiBaseURL === 'function'
        ? apiBaseURL.bind(vm)
        : () => getByPath(vm, apiBaseURL)

    return () => apiBaseURLBuilder();
};

/**
 * Tries create new service instance with `new` constructor, otherwise with factory if failed
 *
 * @return {IApiService}
 */
const createServiceInstance = (vm, ServiceConstructorOrFactory, serviceOptions) => {
    const { apiBaseURL, ...restServiceOptions } = serviceOptions;
    const options = {
        ...restServiceOptions,
        apiBaseURL: createApiBaseURLBuilder(vm, apiBaseURL).call(vm)
    };
    if (Object.isPrototypeOf.call(BaseApiService, ServiceConstructorOrFactory)) {
        return new ServiceConstructorOrFactory({ options });
    }
    return ServiceConstructorOrFactory(options);
};

/**
 * Creates Vue Mixin with specified service factory or identifier and service-related behaviour
 * @return {{ mixin: IApiServiceMixinInstance }}
 */
export const useApiService = (serviceConstructorOrFactory, serviceOptions, mixinOptions = {}) => {
    const { apiBaseURL = API_URL_ACCESSOR_NAME } = serviceOptions;
    const { name: $apiService = PUBLIC_ACCESSOR_NAME } = mixinOptions;

    /**
     */
    const VueMixinComponentOptions = {
        created() {
            this[$apiService] = createServiceInstance(this, serviceConstructorOrFactory, serviceOptions);
            // add to service context info (elem.id) to use in ApiService.client request options build
            this[$apiService].setOptions({
                context: {
                    elemId: this.id
                }
            });
            if (this.isEditorMode) {
                // prettier-ignore
                this.$watch(
                    createApiBaseURLBuilder(this, apiBaseURL),
                    (baseURL) => { this[$apiService].apiBaseURL = baseURL; },
                    { immediate: true }
                );
            }
        },
        /**
         * @this {IApiServiceMixinInstance}
         */
        destroyed() {
            this[$apiService].dispose();
        }
    };

    return {
        mixin: VueMixinComponentOptions
    };
};

/**
 *
 * @param serviceConstructorOrFactory
 * @param serviceOptions
 * @param mixinOptions
 * @return {IApiServiceMixinInstance}
 */
export const useApiServiceMixin = (serviceConstructorOrFactory, serviceOptions, mixinOptions = {}) => {
    return useApiService(serviceConstructorOrFactory, serviceOptions, mixinOptions).mixin;
};
